import { zodResolver } from '@hookform/resolvers/zod';
import { Turnstile } from '@marsidev/react-turnstile';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { signIn } from '../auth/client.js';

const Schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type FormData = z.infer<typeof Schema>;

export function Login() {
  const { t } = useTranslation('auth');
  const { t: te } = useTranslation('errors');
  const [params] = useSearchParams();
  const nav = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(Schema) });
  const [token, setToken] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (data) => {
    setServerError(null);
    if (!token) {
      setServerError(te('captcha_failed'));
      return;
    }
    try {
      const res = await signIn.email({
        email: data.email,
        password: data.password,
        callbackURL: params.get('return') ?? '/app',
        fetchOptions: { headers: { 'cf-turnstile-response': token } },
      });
      // Better Auth returns { error } on failure rather than throwing
      const resErr = (res as { error?: { message?: string; code?: string } | null }).error;
      if (resErr) {
        const code = resErr.code?.toLowerCase() ?? 'invalid_credentials';
        setServerError(te(code, { defaultValue: resErr.message ?? te('invalid_credentials') }));
        return;
      }
      const data2 = (res as { data?: { twoFactorRedirect?: boolean } }).data;
      if (data2?.twoFactorRedirect) {
        nav('/mfa/verify');
      } else {
        nav(params.get('return') ?? '/app');
      }
    } catch (e: unknown) {
      const err = e as { error?: string; message?: string };
      setServerError(te(err.error ?? 'invalid_credentials', { defaultValue: err.message }));
    }
  });

  return (
    <div className="mx-auto mt-16 max-w-sm rounded border bg-white p-6">
      <h1 className="mb-4 text-xl font-semibold">{t('login')}</h1>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="text-sm">{t('email')}</span>
          <input
            type="email"
            {...register('email')}
            className="mt-1 w-full rounded border px-3 py-2"
          />
          {errors.email && <span className="text-xs text-red-600">{errors.email.message}</span>}
        </label>
        <label className="block">
          <span className="text-sm">{t('password')}</span>
          <input
            type="password"
            {...register('password')}
            className="mt-1 w-full rounded border px-3 py-2"
          />
        </label>
        <Turnstile siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''} onSuccess={setToken} />
        {serverError && <p className="text-sm text-red-600">{serverError}</p>}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded bg-blue-600 py-2 text-white disabled:opacity-50"
        >
          {t('login')}
        </button>
      </form>
      <div className="mt-3 flex justify-between text-sm">
        <Link to="/forgot-password">{t('forgotPassword')}</Link>
        <Link to="/signup">{t('signup')}</Link>
      </div>
    </div>
  );
}
